import {
  dec,
  sumDecimals,
  type EntryFull,
  type EntryStatus,
  type JobCostCode,
} from '@siteday/shared';

import { toCsv } from '@/lib/csv';
import { weekDays } from '@/lib/dates';

/** W9 matrices built client-side from the week's full entries (doc 09 Weekly roll-up data). */

export interface MatrixRow {
  key: string;
  label: string;
  sub?: string | undefined;
  unit?: string | undefined;
  byDay: Array<string | null>;
  total: string;
  planPerWeek?: string | null | undefined;
}

export interface WeekRollup {
  days: string[];
  quantities: MatrixRow[];
  labor: MatrixRow[];
  equipment: MatrixRow[];
  laborByDay: Array<string | null>;
  machineByDay: Array<string | null>;
  laborTotal: string;
  machineTotal: string;
  dayStatus: Array<EntryStatus | 'none'>;
  events: Array<{ type: string; count: number; minutes: number }>;
  photoCount: number;
  finishedCount: number;
  entries: EntryFull[];
}

const sumOrNull = (xs: string[]): string | null => (xs.length ? sumDecimals(xs).toString() : null);

/** Lowest status wins per day so a needs_review day never shows as approved. */
const STATUS_RANK: Record<EntryStatus, number> = {
  draft: 0,
  needs_review: 1,
  finished: 2,
  synced: 3,
  approved: 4,
};

export function buildRollup(
  weekStart: string,
  entries: EntryFull[],
  costCodes: JobCostCode[],
): WeekRollup {
  const days = weekDays(weekStart);
  const byDay = (date: string) => entries.filter((e) => e.entryDate === date);
  const workingDays = days.filter((d) => byDay(d).length > 0).length || 5;

  const quantities: MatrixRow[] = costCodes
    .filter(
      (c) =>
        !c.archived ||
        entries.some((e) =>
          e.quantityLines.some((q) => q.jobCostCodeId === c.id && Number(q.quantity) !== 0),
        ),
    )
    .map((c) => {
      const perDay = days.map((d) =>
        sumOrNull(
          byDay(d).flatMap((e) =>
            e.quantityLines
              .filter((q) => q.jobCostCodeId === c.id && Number(q.quantity) !== 0)
              .map((q) => q.quantity),
          ),
        ),
      );
      return {
        key: c.id,
        label: c.code,
        sub: `${c.description} · ${c.unit}`,
        unit: c.unit,
        byDay: perDay,
        total: sumDecimals(perDay.filter((v): v is string => v !== null)).toString(),
        planPerWeek: c.plannedQtyPerDay
          ? dec(c.plannedQtyPerDay).mul(workingDays).toString()
          : null,
      };
    });

  const people = new Map<string, string>();
  const machines = new Map<string, string>();
  for (const e of entries) {
    for (const l of e.crewLines) people.set(l.userId, l.userName);
    for (const l of e.equipmentLines)
      machines.set(l.equipmentId, `${l.equipmentName}${l.unitNumber ? ` · ${l.unitNumber}` : ''}`);
  }
  const labor: MatrixRow[] = [...people.entries()].map(([id, name]) => {
    const perDay = days.map((d) =>
      sumOrNull(
        byDay(d).flatMap((e) => e.crewLines.filter((l) => l.userId === id).map((l) => l.hours)),
      ),
    );
    return {
      key: id,
      label: name,
      byDay: perDay,
      total: sumDecimals(perDay.filter((v): v is string => v !== null)).toString(),
    };
  });
  const equipment: MatrixRow[] = [...machines.entries()].map(([id, name]) => {
    const perDay = days.map((d) =>
      sumOrNull(
        byDay(d).flatMap((e) =>
          e.equipmentLines.filter((l) => l.equipmentId === id).map((l) => l.hours),
        ),
      ),
    );
    return {
      key: id,
      label: name,
      byDay: perDay,
      total: sumDecimals(perDay.filter((v): v is string => v !== null)).toString(),
    };
  });

  const laborByDay = days.map((d) =>
    sumOrNull(byDay(d).flatMap((e) => e.crewLines.map((l) => l.hours))),
  );
  const machineByDay = days.map((d) =>
    sumOrNull(byDay(d).flatMap((e) => e.equipmentLines.map((l) => l.hours))),
  );
  const dayStatus = days.map((d) => {
    const es = byDay(d);
    if (es.length === 0) return 'none' as const;
    return es.reduce<EntryStatus>(
      (acc, e) => (STATUS_RANK[e.status] < STATUS_RANK[acc] ? e.status : acc),
      es[0]!.status,
    );
  });
  const eventMap = new Map<string, { count: number; minutes: number }>();
  for (const e of entries)
    for (const ev of e.events) {
      const cur = eventMap.get(ev.type) ?? { count: 0, minutes: 0 };
      eventMap.set(ev.type, {
        count: cur.count + 1,
        minutes: cur.minutes + (ev.durationMinutes ?? 0),
      });
    }
  return {
    days,
    quantities,
    labor,
    equipment,
    laborByDay,
    machineByDay,
    laborTotal: sumDecimals(laborByDay.filter((v): v is string => v !== null)).toString(),
    machineTotal: sumDecimals(machineByDay.filter((v): v is string => v !== null)).toString(),
    dayStatus,
    events: [...eventMap.entries()].map(([type, v]) => ({ type, ...v })),
    photoCount: entries.reduce((a, e) => a + e.photos.length, 0),
    finishedCount: entries.filter((e) => e.status === 'finished' || e.status === 'synced').length,
    entries,
  };
}

/** CSV per doc 09: job, week_start, date, entry_id, foreman, status, section, code_or_name, unit, value, notes. */
export function rollupCsv(jobName: string, weekStart: string, entries: EntryFull[]): string {
  const rows: Array<Array<string | number | null>> = [
    [
      'job',
      'week_start',
      'date',
      'entry_id',
      'foreman',
      'status',
      'section',
      'code_or_name',
      'unit',
      'value',
      'notes',
    ],
  ];
  for (const e of [...entries].sort((a, b) => a.entryDate.localeCompare(b.entryDate))) {
    for (const q of e.quantityLines)
      if (Number(q.quantity) !== 0)
        rows.push([
          jobName,
          weekStart,
          e.entryDate,
          e.id,
          e.foremanName,
          e.status,
          'quantity',
          q.code,
          q.unit,
          q.quantity,
          e.notes,
        ]);
    for (const l of e.crewLines)
      rows.push([
        jobName,
        weekStart,
        e.entryDate,
        e.id,
        e.foremanName,
        e.status,
        'labor',
        l.userName,
        'HR',
        l.hours,
        null,
      ]);
    for (const l of e.equipmentLines)
      rows.push([
        jobName,
        weekStart,
        e.entryDate,
        e.id,
        e.foremanName,
        e.status,
        'equipment',
        l.equipmentName,
        'HR',
        l.hours,
        null,
      ]);
    for (const ev of e.events)
      rows.push([
        jobName,
        weekStart,
        e.entryDate,
        e.id,
        e.foremanName,
        e.status,
        'event',
        ev.type,
        'MIN',
        ev.durationMinutes,
        ev.description,
      ]);
  }
  return toCsv(rows);
}
